import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/bits";

const EASE = [0.76, 0, 0.24, 1];

export default function Hero() {
  const ref = useRef(null);
  const [intro, setIntro] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 1500);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] overflow-hidden" data-testid="hero-section">
      <AnimatePresence>
        {intro && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#022C22]"
            exit={{ y: "-100%" }}
            transition={{ duration: 1.1, ease: EASE }}
            data-testid="intro-curtain"
          >
            <MaskedLine delay={0.25}>
              <span className="font-display text-3xl tracking-[0.3em] text-[#FAF9F6] sm:text-5xl">
                EMBASSY
              </span>
            </MaskedLine>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <motion.img
          src="/images/hero.jpg"
          alt="Aerial view of Embassy South Reserve at dusk, nine towers woven through forest beside Tarahunise Lake"
          data-testid="hero-image"
          className="h-full w-full object-cover"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.4, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#02120D]/95 via-[#02120D]/35 to-[#02120D]/50" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex h-full flex-col justify-end pb-24 md:pb-28"
      >
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
          <MaskedLine delay={1.7}>
            <span className="text-[11px] font-medium uppercase tracking-[0.42em] text-[#A98B5F]">
              Embassy South Reserve · North Bengaluru
            </span>
          </MaskedLine>
          <h1 className="font-display mt-6 text-[13vw] font-light leading-[0.98] tracking-tight text-[#F5F1E8] sm:text-7xl lg:text-[6.5rem]">
            <MaskedLine delay={1.85}>Planned around nature.</MaskedLine>
            <MaskedLine delay={2.0}>
              <span className="italic">Not the other way around.</span>
            </MaskedLine>
          </h1>
          <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
            <MaskedLine delay={2.2}>
              <p className="max-w-md text-sm leading-relaxed text-[#F5F1E8]/75 md:text-base">
                Studio to 3.5 BHK residences at Embassy Origins — beside Tarahunise Lake.
              </p>
            </MaskedLine>
            <motion.button
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => document.querySelector("#enquire")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-enquire-btn"
              className="w-fit rounded-full bg-[#F5F1E8] px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.26em] text-[#022C22] transition-transform duration-300 hover:scale-105"
            >
              Register Interest
            </motion.button>
          </div>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        onClick={() => document.querySelector("#destination")?.scrollIntoView({ behavior: "smooth" })}
        data-testid="hero-scroll-cue"
        aria-label="Scroll to explore"
        className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-[#F5F1E8]/70 transition-colors duration-300 hover:text-[#F5F1E8]"
      >
        <span className="text-[9px] uppercase tracking-[0.44em]">Scroll</span>
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
}
